// Hooks for the multi-dataset Report Builder (the new `/reports` API).
// NOTE: distinct from `use-reports.js`, which targets the legacy `audit_reports` resource.
import { useContext } from 'react'
import { useQuery, useInfiniteQuery, useMutation } from 'react-query'
import { apiGet, apiPost, apiPatch, apiDelete } from 'api'
import AuthContext from 'context/auth-context'
import UserContext from 'context/user-context'

const getReports = async (token, userId, params) => {
  const url = `users/${userId}/reports`
  const data = await apiGet({ url, params, token })
  return data
}

export function useReports({ params, options } = {}) {
  const token = useContext(AuthContext)
  const { id } = useContext(UserContext)
  const queryFn = () => getReports(token, id, params)
  const queryKey = ['reports-builder', params]
  return useQuery(queryKey, queryFn, { ...options })
}

const getReport = async (token, id) => {
  const url = `reports/${id}`
  const data = await apiGet({ url, token })
  return data
}

export function useReport({ id, options } = {}) {
  const token = useContext(AuthContext)
  const queryFn = () => getReport(token, id)
  const queryKey = [`reports/${id}`]
  return useQuery(queryKey, queryFn, { enabled: !!id, ...options })
}

const createReport = async (token, userId, params) => {
  const url = `users/${userId}/reports`
  const objectName = 'report'
  const data = await apiPost({ url, token, objectName, params })
  return data
}

export function useCreateReport({ options } = {}) {
  const token = useContext(AuthContext)
  const { id } = useContext(UserContext)
  return useMutation((params) => createReport(token, id, params), { ...options })
}

const updateReport = async (token, id, params) => {
  const url = `reports/${id}`
  const objectName = 'report'
  const data = await apiPatch({ url, token, objectName, params })
  return data
}

export function useUpdateReport({ options } = {}) {
  const token = useContext(AuthContext)
  return useMutation(({ id, ...params }) => updateReport(token, id, params), { ...options })
}

const destroyReport = async (token, id) => {
  const url = `reports/${id}`
  await apiDelete({ url, token })
}

export function useDestroyReport({ options } = {}) {
  const token = useContext(AuthContext)
  return useMutation((id) => destroyReport(token, id), { ...options })
}

const duplicateReport = async (token, reportId) => {
  const url = `reports/${reportId}/duplications`
  const data = await apiPost({ url, token, params: {} })
  return data
}

export function useDuplicateReport({ options } = {}) {
  const token = useContext(AuthContext)
  return useMutation((reportId) => duplicateReport(token, reportId), { ...options })
}

const setReportLock = async (token, reportId, locked) => {
  const url = `reports/${reportId}/lock`
  return locked ? apiPost({ url, token, params: {} }) : apiDelete({ url, token })
}

// Owner-only: a locked report can't be edited by anyone, the owner included, until it is unlocked.
export function useSetReportLock({ options } = {}) {
  const token = useContext(AuthContext)
  return useMutation(({ reportId, locked }) => setReportLock(token, reportId, locked), { ...options })
}

const getReportDocumentHtml = async (token, reportId) => {
  const url = `reports/${reportId}/document_html`
  const data = await apiGet({ url, token })
  return data
}

// Fetches the print-ready HTML for a document report (for browser print-to-PDF).
export function useReportDocumentHtml({ options } = {}) {
  const token = useContext(AuthContext)
  return useMutation((reportId) => getReportDocumentHtml(token, reportId), { ...options })
}

const createReportPdfExport = async (token, reportId) => {
  const url = `reports/${reportId}/pdf_export`
  const data = await apiPost({ url, token, params: {} })
  return data
}

export function useCreateReportPdfExport({ options } = {}) {
  const token = useContext(AuthContext)
  return useMutation((reportId) => createReportPdfExport(token, reportId), { ...options })
}

const splitReport = async (token, reportId, params) => {
  const url = `reports/${reportId}/splits`
  // apiPost always wraps the body as { [objectName]: params }, so objectName is required — without
  // it the root key is the string "undefined" and the server sees no params at all.
  const data = await apiPost({ url, token, objectName: 'split', params })
  return data
}

// Fans a report out into one read-only child report per value of a single column.
export function useSplitReport({ options } = {}) {
  const token = useContext(AuthContext)
  return useMutation(({ reportId, ...params }) => splitReport(token, reportId, params), { ...options })
}

const getSplitOptions = async (token, reportId, params) => {
  const url = `reports/${reportId}/split_options`
  const data = await apiGet({ url, token, params })
  return data
}

// A segment can carry thousands of codes and the picker shows six rows at a time, so it asks for
// them a page at a time and loads the next as the list is scrolled.
export const SPLIT_OPTIONS_PER_PAGE = 50

// The values a report can be split on: the account codes of one segment, or the municipality's
// account groups. Values only — how much data each one holds comes from useSplitCounts, which is
// not paged with this: its counts cover every value, not the page currently on screen.
export function useSplitOptions({ reportId, dimension, segment, search, options } = {}) {
  const token = useContext(AuthContext)
  const queryKey = ['report-split-options', reportId, dimension, segment, search]
  const queryFn = ({ pageParam = 1 }) =>
    getSplitOptions(token, reportId, {
      dimension,
      segment,
      search,
      page: pageParam,
      perPage: SPLIT_OPTIONS_PER_PAGE
    })

  return useInfiniteQuery(queryKey, queryFn, {
    // From the server's own page count rather than from a short page: the last page is known to
    // be the last as it arrives, so reaching the end of the list costs no extra request.
    getNextPageParam: (lastPage) => {
      const meta = lastPage?.meta
      return meta && meta.page < meta.pages ? meta.page + 1 : undefined
    },
    enabled: !!reportId && !!dimension,
    ...options
  })
}

const getSplitCounts = async (token, reportId, params) => {
  const url = `reports/${reportId}/split_counts`
  const data = await apiGet({ url, token, params })
  return data
}

// How much of the report's data each split value would claim. Its own request, and deliberately
// not keyed on the picker's search: the counts are a scan of every transaction in the report
// (Reports::SplitCounts), while the values beside them are a lookup. Together they made the list
// wait on the scan and re-run it on every keystroke — apart, the list renders at once and this
// fills it in when it lands, or says it couldn't.
export function useSplitCounts({ reportId, dimension, segment, options } = {}) {
  const token = useContext(AuthContext)
  const params = { dimension, segment }
  const queryKey = ['report-split-counts', reportId, dimension, segment]
  const queryFn = () => getSplitCounts(token, reportId, params)
  return useQuery(queryKey, queryFn, { enabled: !!reportId && !!dimension, ...options })
}

const unlinkReport = async (token, reportId) => {
  const url = `reports/${reportId}/unlink`
  const data = await apiPost({ url, token, params: {} })
  return data
}

// Cuts a child report loose from its parent: it keeps its data and becomes editable.
export function useUnlinkReport({ options } = {}) {
  const token = useContext(AuthContext)
  return useMutation((reportId) => unlinkReport(token, reportId), { ...options })
}

const regenerateReportText = async (token, reportId) => {
  const url = `reports/${reportId}/regenerate_text`
  const data = await apiPost({ url, token, objectName: 'report', params: {} })
  return data
}

// Rewrites child narrative against child figures — for one child, or every child of a template.
export function useRegenerateReportText({ options } = {}) {
  const token = useContext(AuthContext)
  return useMutation((reportId) => regenerateReportText(token, reportId), { ...options })
}
